/* Vision LANE — which vision-service publisher Legend draws.
 *
 *   'mv5'  → WS `vision-mv5:{SYM}`, REST `?lane=mv5` (`worker:vision-mv5:{SYM}`):
 *            the vision-service-mv5 deployment (VISION_PUBLISH_SUFFIX=-mv5).
 *            The only lane since MV-4 was retired on 2026-10-25.
 *
 * MV-4's unsuffixed lane ('main': WS `vision:{SYM}`, `worker:vision:{SYM}`) went
 * with MV-4, and the Vision pill's MV-5 | MV-4 toggle with it. A browser that
 * stored 'main' — or the 2026-09-17 toggle's 'live' | 'mv4' — reads as MV-5.
 * The store stays so a future shadow lane is one more entry in VISION_LANES:
 * the consumers (VisionLayer, Terminal's structure rail, VerdictRibbon,
 * LadderCard) still re-key their hydrate + subscribe on the lane and reset
 * their seq/ts guard, because two publishers count seq independently.
 *
 * Per browser: localStorage `legend.visionLane`. Within a page every consumer
 * is told directly (onVisionLane / use-vision-lane.ts); other windows of the
 * origin follow through the browser's `storage` event.
 *
 * No imports and no DOM types: lib/__tests__ compile this under the root jest
 * config (lib ES2022), so browser globals are reached through globalThis, as
 * lib/trendlines.ts does. */

export type VisionLane = 'mv5';
export const VISION_LANES: readonly VisionLane[] = ['mv5'];
export const DEFAULT_VISION_LANE: VisionLane = 'mv5';
export const VISION_LANE_KEY = 'legend.visionLane';

/** What each lane is called: the model it carries. The status pill's model
 *  chip shows what the FRAME says it is. */
export const LANE_LABEL: Record<VisionLane, string> = { mv5: 'MV-5' };

export function parseLane(v: unknown): VisionLane | null {
  return v === 'mv5' ? v : null;
}

/** Where the lane is kept and where other windows' writes are heard. */
export interface LaneEnv {
  storage: { getItem(k: string): string | null; setItem(k: string, v: string): void } | null;
  /** The window — the browser fires `storage` there for OTHER windows' writes. */
  target: { addEventListener(type: string, fn: (e: any) => void): void } | null;
}

export interface LaneStore {
  get(): VisionLane;
  set(lane: VisionLane): void;
  /** Called on every change, own or another window's. Returns the unsubscribe. */
  on(fn: (lane: VisionLane) => void): () => void;
}

export function createVisionLaneStore(env: LaneEnv): LaneStore {
  const listeners = new Set<(lane: VisionLane) => void>();
  let current: VisionLane = DEFAULT_VISION_LANE;
  try { current = parseLane(env.storage ? env.storage.getItem(VISION_LANE_KEY) : null) || DEFAULT_VISION_LANE; } catch { /* storage blocked */ }

  const apply = (next: VisionLane): void => {
    if (next === current) return;
    current = next;
    for (const fn of [...listeners]) fn(next);
  };

  if (env.target) {
    env.target.addEventListener('storage', (e: any) => {
      if (!e || (e.key !== VISION_LANE_KEY && e.key !== null)) return;   // key null = storage.clear()
      if (e.storageArea && env.storage && e.storageArea !== env.storage) return;  // sessionStorage
      apply(parseLane(e.newValue) || DEFAULT_VISION_LANE);
    });
  }

  return {
    get: () => current,
    set: (lane) => {
      try { if (env.storage) env.storage.setItem(VISION_LANE_KEY, lane); } catch { /* private mode: the page still switches */ }
      apply(lane);
    },
    on: (fn) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
  };
}

function browserEnv(): LaneEnv {
  const g: any = globalThis;
  let storage: LaneEnv['storage'] = null;
  try { storage = g.localStorage || null; } catch { storage = null; }   // throws when site data is blocked
  return { storage, target: typeof g.addEventListener === 'function' ? g : null };
}

// The page's one store (a detached window has its own, synced by `storage`).
const store = createVisionLaneStore(browserEnv());

export function getVisionLane(): VisionLane { return store.get(); }
export function setVisionLane(lane: VisionLane): void { store.set(lane); }
export function onVisionLane(fn: (lane: VisionLane) => void): () => void { return store.on(fn); }

/** WS channel for a lane: what a vision-service with VISION_PUBLISH_SUFFIX=-<lane> publishes. */
export function laneChannel(symbol: string, lane: VisionLane): string {
  return `vision-${lane}:${symbol}`;
}

/** `?lane=` for the hydrate route. */
export function laneParam(lane: VisionLane): string {
  return lane;
}

/** The lane a WS frame came from: realtime stamps `lane` on every frame it
 *  relays, so an unstamped one (the retired unsuffixed channel) matches no lane. */
export function frameLane(m: any): string {
  return m && typeof m.lane === 'string' && m.lane ? m.lane : '';
}
